// CalendarsPage.js
import React, { useState, useMemo } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { 
  EarningsCalendarCard, 
  EconomicCalendarCard, 
  useEarningsCalendar // <--- Import the hook you already created!
} from '../components/CalendarComponents';

//import { WeekAhead } from '../components/WeekAhead';
import { useLayoutMaster } from '../utils/useLayoutMaster';
import WeekAhead from "../components/Week_Ahead";



export const CalendarsPage = () => {
  
  const layout = useLayoutMaster();
  const [isDark, setIsDark] = useState(true);
  
  // 1. REUSE your existing hook
  const { data: earningsData, loading } = useEarningsCalendar();

  const theme = {
    background: isDark ? '#080808' : '#f9fafb',
    textPrimary: isDark ? '#fff' : '#111827',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.1)',
    borderHover: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.05)',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.9)',
    accent: isDark ? '#10B981' : '#059669',
  };

  // 2. TRANSFORM the nested data for the Header Ticker
  const getEarningsTickers = useMemo(() => {
    if (!earningsData || !Array.isArray(earningsData)) return [];

    const flatList = [];

    // Loop through days
    earningsData.forEach(day => {
      if (!day.groups) return;
      
      // Loop through times (Pre, During, Post)
      ['Pre Market', 'During Market', 'Post Market'].forEach(timeSlot => {
        if (day.groups[timeSlot]) {
          day.groups[timeSlot].forEach(company => {
            flatList.push({
              ticker: company.Ticker,
              // Format: "NVIDIA (Post Market)" to give context in the ticker
              name: `${company.longName || company.Ticker} • ${timeSlot}`,
              ult: 0, // Calendar API likely doesn't have price, defaulting to 0
              var: 0 
            });
          });
        }
      });
    });

    // Return top 20 upcoming earnings for the ticker
    return flatList.slice(0, 20);
  }, [earningsData]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: theme.background, 
      color: theme.textPrimary, 
      position: 'relative' 
    }}>
      
      {/* Background Effect — opacity 0.4 to match home page darkness */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none', opacity: 0.4 }}>
        <Silk speed={20} scale={1.2} color="#6660fe" noiseIntensity={2} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header 
          theme={theme} 
          isDark={isDark} 
          toggleTheme={() => setIsDark(!isDark)} 
          // 3. Pass the flattened earnings list here
          topTickers={getEarningsTickers} 
        />
        
        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: layout.isMobile ? '1.5rem 1rem' : '4rem 2rem' }}>
          {/* WEEK AHEAD SECTION */}
<div style={{ marginBottom: '4rem' }}>
  <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
    <span style={{ fontSize: '1.8rem' }}>📅</span> The Week Ahead
  </h2>
  {/* <WeekAhead theme={theme} /> */}
  <WeekAhead
  src="/api/week-ahead/latest"
  imgBase="/week_ahead_images"
  logoBase="/api"
/>
</div>
          {/* Header Section */}
          <div style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <h1 style={{ 
                fontFamily: "'Roble', system-ui, sans-serif",
                fontSize: layout.isMobile ? '2rem' : '3rem', 
                fontWeight: 800, 
                margin: 0, 
                background: 'linear-gradient(to right, #10B981, #34D399)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent' 
              }}>
                Market Calendars
              </h1>
              <span style={{ 
                fontFamily: "'IBM Plex Mono', monospace",
                background: 'rgba(16, 185, 129, 0.1)', 
                color: '#10B981', 
                padding: '4px 12px', 
                borderRadius: '100px', 
                fontSize: '0.8rem', 
                fontWeight: 'bold' 
              }}>
                {loading ? 'LOADING...' : 'LIVE'}
              </span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: theme.textSecondary, fontSize: '1.2rem', maxWidth: '800px' }}>
              Stay ahead of market movements with real-time earnings releases and major economic events.
            </p>
          </div>

          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />



          {/* Calendars Grid - Side by Side Layout */}
          <section style={{ marginBottom: '3rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: layout.isMobile ? '1fr' : 'repeat(auto-fit, minmax(450px, 1fr))', 
              gap: '2rem',
              alignItems: 'start' 
            }}>
              {/* Earnings Calendar */}
              <div style={{ flex: 1 }}>
                <EarningsCalendarCard theme={theme} maxDays={14} />
              </div>

              {/* Economic Calendar */}
              <div style={{ flex: 1 }}>
                <EconomicCalendarCard theme={theme} maxEvents={20} />
              </div>
            </div>
          </section>

          {/* Reference Guide */}
          <section style={{
            marginTop: '4rem',
            padding: '2rem',
            background: theme.cardBg,
            borderRadius: '16px',
            border: `1px solid ${theme.border}`,
            backdropFilter: 'blur(10px)'
          }}>
            <h3 style={{
              fontFamily: "'Roble', system-ui, sans-serif",
              fontSize: '1.2rem',
              fontWeight: 800,
              color: theme.accent,
              marginBottom: '1.5rem',
            }}>
              ℹ️ Reference Guide
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: layout.isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontWeight: 400,
              color: theme.textSecondary,
              fontSize: '0.95rem',
              lineHeight: '1.6'
            }}>
              <div>
                <strong style={{ fontFamily: "'Roble', system-ui, sans-serif", fontWeight: 800, color: theme.textPrimary, display: 'block', marginBottom: '0.5rem' }}>
                  📊 Earnings Calendar
                </strong>
                <p style={{ margin: 0 }}>
                  Tracks confirmed company earnings releases.
                </p>
                <ul style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
                  <li>Pre Market (Before 9:30 AM ET)</li>
                  <li>During Market</li>
                  <li>Post Market (After 4:00 PM ET)</li>
                </ul>
              </div>

              <div>
                <strong style={{ fontFamily: "'Roble', system-ui, sans-serif", fontWeight: 800, color: theme.textPrimary, display: 'block', marginBottom: '0.5rem' }}>
                  📅 Economic Calendar
                </strong>
                <p style={{ margin: 0 }}>
                  Global macroeconomic events.
                </p>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#ef4444' }}>●</span> High
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#f59e0b' }}>●</span> Med
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#6b7280' }}>●</span> Low
                  </div>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
};

export default CalendarsPage;